import React from "react";
import { X } from "lucide-react";

/**
 * Deterministic avatar background palette consistent with SynexOS design system.
 */
const getAvatarColor = (name = "") => {
  const colors = [
    "bg-blue-500",
    "bg-indigo-500",
    "bg-purple-500",
    "bg-sky-500",
    "bg-teal-500",
    "bg-cyan-500",
    "bg-blue-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Single, clean, brand-blue SaaS Notification Toast for SynexOS Workspace Messages
 */
const NotificationToast = ({ notification, onClose, onNavigate }) => {
  if (!notification) return null;

  const rawMessage = notification.message || notification.content || "";
  let senderName = "";
  let previewText = rawMessage;

  // Extract sender name from "Sender: message" prefix if present
  if (rawMessage && rawMessage.includes(": ")) {
    const colonIdx = rawMessage.indexOf(": ");
    senderName = rawMessage.slice(0, colonIdx).trim();
    previewText = rawMessage.slice(colonIdx + 2).trim();
  } else {
    senderName = notification.title || "U";
  }

  const initial = (senderName || "U").trim().charAt(0).toUpperCase() || "U";
  const avatarBg = getAvatarColor(senderName);
  const displayTitle = notification.title || "New notification";

  const handleClick = (e) => {
    if (e.defaultPrevented) return;
    if (typeof onNavigate === "function") {
      onNavigate();
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <div
      role="alert"
      onClick={handleClick}
      className="workspace-custom-toast-card relative w-[330px] sm:w-[350px] max-w-[calc(100vw-2rem)] bg-[#033F99] text-white rounded-[12px] border border-white/15 shadow-[0_10px_25px_-5px_rgba(3,63,153,0.35),0_8px_10px_-6px_rgba(3,63,153,0.35)] pl-3.5 pr-3 py-2.5 flex items-start gap-2.5 cursor-pointer hover:bg-[#02337d] transition-all select-none group animate-in fade-in slide-in-from-right-3 duration-200 overflow-hidden font-sans"
    >
      {/* Left: Sender Avatar */}
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0 shadow-sm border border-white/20 ${avatarBg}`}
      >
        {initial}
      </div>

      {/* Center: Title & Truncated Preview */}
      <div className="flex-1 min-w-0 pr-1">
        <p className="text-[13px] font-semibold text-white truncate leading-snug">
          {displayTitle}
        </p>
        {previewText && (
          <p className="text-[12px] text-white/85 truncate leading-tight mt-0.5">
            {previewText}
          </p>
        )}
      </div>

      {/* Right: Timestamp & Close Button */}
      <div className="flex items-center gap-1.5 shrink-0 self-start pt-0.5">
        <span className="text-[11px] text-white/70 font-medium whitespace-nowrap">
          Just now
        </span>
        <button
          type="button"
          onClick={handleClose}
          className="p-1 rounded-md text-white/70 hover:text-white hover:bg-white/15 transition-colors border-0 bg-transparent cursor-pointer flex items-center justify-center"
          title="Dismiss"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>
      </div>
    </div>
  );
};

export default NotificationToast;

